
import { computed, defineComponent, getCurrentInstance, onMounted, onUnmounted, PropType, ref, watch } from 'vue'
import store from '@/store/index'

export type IntegrationRequestResultModalType = 'registered' | 'alreadyExists' | 'sent'

export default defineComponent({
    name: 'IntegrationRequestResultModal',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String as PropType<IntegrationRequestResultModalType>,
            required: true,
        },
        email: {
            type: String,
            default: '',
        },
    },
    setup(props, { emit }) {
        const root = getCurrentInstance()!.proxy as {
            $auth?: {
                login?: (
                    email: string,
                    password: string,
                ) => Promise<{ error?: { message?: string } }>
            }
        }

        const loginEmail = ref('')
        const loginPassword = ref('')
        const loginError = ref('')
        const isLoggingIn = ref(false)

        const modalTitle = computed(() => {
            if (props.type === 'registered') return 'Ваша заявка отправлена'
            return 'Заявка отправлена'
        })

        const modalSubtitle = computed(() => {
            if (props.type === 'registered') {
                return 'В ближайшее время с вами свяжется менеджер.<br>Также пароль от Вашего личного кабинета выслан на Вашу почту, используйте его для входа в систему.'
            }
            if (props.type === 'alreadyExists') {
                return 'В ближайшее время с вами свяжется менеджер.<br><b>Вы уже являетесь зарегистрированным пользователем</b> — можете авторизоваться с помощью почты, которую указали в заявке.'
            }
            return 'В ближайшее время с вами свяжется менеджер для уточнения деталей.'
        })

        const iconToneClass = computed(() => (props.type === 'sent' ? 'irr-modal__icon--good' : 'irr-modal__icon--indigo'))

        const canSubmitLogin = computed(() => {
            return Boolean(loginEmail.value.trim() && loginPassword.value)
        })

        const resetLoginForm = () => {
            loginEmail.value = props.email || ''
            loginPassword.value = ''
            loginError.value = ''
            isLoggingIn.value = false
        }

        const close = () => {
            if (isLoggingIn.value) return
            emit('input', false)
        }

        const onBackdropClick = () => {
            if (props.type === 'alreadyExists' && isLoggingIn.value) return
            close()
        }

        const onEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || !props.value) return
            onBackdropClick()
        }

        onMounted(() => {
            window.addEventListener('keydown', onEscape)
        })

        onUnmounted(() => {
            window.removeEventListener('keydown', onEscape)
        })

        const reloadCurrentPage = () => {
            window.location.reload()
        }

        const submitLogin = async () => {
            if (isLoggingIn.value || !canSubmitLogin.value) return
            if (!root.$auth?.login) {
                loginError.value = 'Авторизация недоступна'
                return
            }

            isLoggingIn.value = true
            loginError.value = ''

            try {
                const { error } = await root.$auth.login(loginEmail.value.trim(), loginPassword.value)
                if (error) {
                    loginError.value = error.message || 'Неверная почта или пароль'
                    return
                }
                emit('login-success')
                emit('input', false)
                reloadCurrentPage()
            } catch {
                loginError.value = 'Не удалось выполнить вход. Попробуйте позже.'
            } finally {
                isLoggingIn.value = false
            }
        }

        const openForgotPassword = () => {
            const email = loginEmail.value.trim() || props.email.trim()
            if (email) {
                store.commit('setAuthDialogPrefillEmail', email)
            }
            store.commit('setAuthDialogStartSection', 'passwordReset')
            store.commit('setIsAuthDialog', true)
            emit('input', false)
        }

        watch(
            () => [props.value, props.type, props.email] as const,
            ([isOpened]) => {
                if (isOpened) resetLoginForm()
            },
            { immediate: true },
        )

        return {
            loginEmail,
            loginPassword,
            loginError,
            isLoggingIn,
            modalTitle,
            modalSubtitle,
            iconToneClass,
            canSubmitLogin,
            close,
            onBackdropClick,
            onEscape,
            submitLogin,
            openForgotPassword,
        }
    },
})
